import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import type { RefObject } from 'react';
import {
  firstModalValidationFieldId,
  modalValidationSummary,
  scrollModalBodyToTarget,
  type ModalFieldErrors,
} from '../utils/modalFormValidation';

export function useModalFormValidation(containerRef: RefObject<HTMLElement | null>) {
  const [fieldErrors, setFieldErrors] = useState<ModalFieldErrors>({});
  const [bannerError, setBannerError] = useState<string | null>(null);

  const clearErrors = useCallback(() => {
    setFieldErrors({});
    setBannerError(null);
  }, []);

  const reportValidation = useCallback(
    (errors: ModalFieldErrors, options?: { message?: string; firstFieldId?: string }) => {
      const filtered = Object.fromEntries(
        Object.entries(errors).filter(([, message]) => Boolean(message))
      );
      if (Object.keys(filtered).length === 0) return false;

      const message = options?.message ?? modalValidationSummary(filtered);
      const firstFieldId = options?.firstFieldId ?? firstModalValidationFieldId(filtered);

      flushSync(() => {
        setFieldErrors(filtered);
        setBannerError(message);
      });

      scrollModalBodyToTarget(
        containerRef.current,
        firstFieldId ? { elementId: firstFieldId } : { scrollTop: true }
      );
      return true;
    },
    [containerRef]
  );

  const reportApiError = useCallback(
    (message: string, bannerId?: string) => {
      flushSync(() => {
        setFieldErrors({});
        setBannerError(message);
      });
      scrollModalBodyToTarget(
        containerRef.current,
        bannerId ? { elementId: bannerId } : { scrollTop: true }
      );
    },
    [containerRef]
  );

  const revalidateIfNeeded = useCallback((nextErrors: ModalFieldErrors) => {
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      return nextErrors;
    });
    setBannerError((prev) => {
      if (!prev) return prev;
      return modalValidationSummary(nextErrors);
    });
  }, []);

  return {
    fieldErrors,
    bannerError,
    setBannerError,
    setFieldErrors,
    clearErrors,
    reportValidation,
    reportApiError,
    revalidateIfNeeded,
  };
}
